import { shopSimulateSplit } from 'api/user';
import { numberAdd, numberDivide, numberMutiply } from 'libs/compute';
import { ORDER_SOURCE } from 'libs/cons';
import { LocContext, OrderContext, MediaQueryContext } from 'libs/contexts';
import { PageMallSkuDetail } from 'libs/types';
import { formatPrice, formatRenderTotalPrice, formatTotalPrice } from 'libs/utils';
import router from 'next/router';
import { useContext, useRef } from 'react';

type HandleCreateProps = {
  skuList: Array<MmuMallSku>;
  selectTradeAgreement: TradeAgreementInfo;
  shopInfo: Record<string, string>;
  skuDetail: PageMallSkuDetail;
  selectAccountPeriod: DiscountByMethodInfo;
  selectStation: Station;
  source?: string;
};

export default function useProductDetail() {
  const { location } = useContext(LocContext);
  const { setOrder } = useContext(OrderContext);
  const { isMobile } = useContext(MediaQueryContext);

  const loading = useRef(false);

  async function handleCreate({
    skuList,
    selectTradeAgreement,
    shopInfo,
    skuDetail,
    selectAccountPeriod,
    selectStation,
    source,
  }: HandleCreateProps) {
    const list = skuList.filter((sku) => {
      let hasPrice = true;
      if (sku.priceWithOutTaxHasInput) {
        hasPrice = sku.priceWithOutTax && sku.priceWithOutTax !== 0;
      }
      if (sku.priceWithTaxHasInput) {
        hasPrice = sku.priceWithTax && sku.priceWithTax !== 0;
      }
      return sku.shopQuantity > 0 && hasPrice && sku.agreementCanSale && sku.areaCanSale;
    });
    const params = {
      areaSysNo: location.sysNo as any,
      type: 0,
      mmuProductEnable: true,
      mobileFlag: isMobile,
      simulateSplitGroup: [
        {
          tradeAgreementSysNo: selectTradeAgreement?.sysNo,
          netPriceType: selectTradeAgreement?.netPriceType,
          shopSysNo: shopInfo.sysNo,
          supplierSysNo: skuDetail.supplierSysNo,
          paymentType: selectAccountPeriod?.accountPeriod,
          billingPeriod: selectAccountPeriod?.billingPeriod,
          groupSysNo: Math.floor(Math.random() * Math.pow(10, 13) + Date.now()).toString(),
          skuList: list.map((v) => {
            const obj = { ...v };
            if (skuDetail.concreteFlag) {
              obj.concreteAttributeList = v.attributeList;
            }
            return obj;
          }),
          stationSysNo: selectStation?.sysNo,
          stationName: selectStation?.name,
          stationNo: selectStation?.stationNo,
          skuType: skuDetail.skuType,
        },
      ],
    };
    loading.current = true;
    const res = await shopSimulateSplit(params);
    loading.current = false;
    if (res.code === 200) {
      setOrder((o: OrderInfo) => ({
        ...o,
        skuParams: {
          ...params,
        },
        formData: null,
        receiveAddress: null,
      }));
      const url =
        source === ORDER_SOURCE.SKU ? `/order/create?source=${ORDER_SOURCE.SKU}` : '/order/create';
      router.push(url);
    }
  }

  function toShop(shopInfo: Record<string, string>) {
    sessionStorage.removeItem(`/shop/index/${shopInfo.sysNo}`);
    router.push(`/shop/index/${shopInfo.sysNo}`);
  }

  function getTotalPrice(skuList: Array<MmuMallSku>) {
    const total = skuList?.reduce((acc, cur) => {
      let { priceWithTax } = cur;
      const { priceWithOutTax } = cur;
      const taxRate = +numberAdd(numberDivide(cur.taxRate, 100), 1);
      if (priceWithTax === null && priceWithOutTax !== null) {
        priceWithTax = +formatPrice(priceWithOutTax * taxRate);
      }
      const val = +formatTotalPrice(+numberMutiply(priceWithTax, cur.shopQuantity));
      if (isNaN(val)) return acc;
      return +numberAdd(acc, val);
    }, 0);
    return total ? formatRenderTotalPrice(total) : '';
  }

  return {
    loading,
    handleCreate,
    toShop,
    getTotalPrice,
  };
}
